// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import userService from "../api/userService";
import axios from "../api/axiosConfig";
import { clearSession, storeSession } from "../api/session";
import { normalizeRole } from "../utils/rolePermissions";

const normalizeUser = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const role = normalizeRole(raw.role ?? raw.userRole);
  if (!role) return null;

  return { ...raw, role };
};

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const PREVIEW_KEY = "bremar-privilege-preview";

function readPreview() {
  try {
    const raw = localStorage.getItem(PREVIEW_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storePreview(next) {
  try {
    if (next) localStorage.setItem(PREVIEW_KEY, JSON.stringify(next));
    else localStorage.removeItem(PREVIEW_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [previewUser, setPreviewUser] = useState(() => readPreview());
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);

  const effectiveUser =
    user?.role === "admin" && previewUser ? previewUser : user;

  // === Cargar perfil al montar si hay token ===
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      // Setear header para axios
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      try {
        const res = await userService.getProfile();
        const userData = normalizeUser(res.data?.user ?? res.data?.data);

        if (!userData) {
          throw new Error("Perfil de usuario inválido");
        }

        setUser(userData);
        if (userData?.role !== "admin") {
          storePreview(null);
          setPreviewUser(null);
        }
      } catch (error) {
        console.error("Error loading user:", error);
        clearSession();
        delete axios.defaults.headers.common.Authorization;
        setUser(null);
        setSessionId(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // === Login ===
  const login = async (email, password) => {
    const res = await userService.login({ email, password });
    const token = res.data?.accessToken ?? res.data?.token;
    const userData = normalizeUser(res.data?.user ?? res.data?.data);
    const session = res.data?.sessionId;
    const refreshToken = res.data?.refreshToken;

    if (!token) throw new Error("No se recibió token del login");
    if (!userData) throw new Error("Perfil de usuario inválido");

    storeSession({ token, refreshToken, sessionId: session });
    if (session) setSessionId(session);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  // === Logout ===
  const logout = async () => {
    // Intentar cerrar la sesión en el backend
    const session = localStorage.getItem("sessionId");
    try {
      await axios.post("/auth/logout", { sessionId: session });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }

    clearSession();
    storePreview(null);
    delete axios.defaults.headers.common.Authorization;
    setUser(null);
    setPreviewUser(null);
    setSessionId(null);
  };

  const setPrivilegePreview = useCallback((nextUser) => {
    if (user?.role !== "admin") return;
    const normalized = normalizeUser(nextUser);
    if (!normalized) return;
    storePreview(normalized);
    setPreviewUser(normalized);
  }, [user?.role]);

  const clearPrivilegePreview = useCallback(() => {
    storePreview(null);
    setPreviewUser(null);
  }, []);

  const value = {
    user,
    previewUser,
    effectiveUser,
    isPreviewing: Boolean(previewUser && user?.role === "admin"),
    sessionId,
    isAuthenticated: Boolean(user),
    role: effectiveUser?.role ?? null,
    login,
    logout,
    setPrivilegePreview,
    clearPrivilegePreview,
    loading,
  };

  // 🔹 Mostrar loader mientras se valida token
  if (loading) return <div>Cargando...</div>;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
